import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, HelpCircle, LogOut, Settings, X, Wallet, History, ShoppingBag } from "lucide-react";
import CreditIcon, { CreditPrice } from "@/components/CreditIcon";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthModals } from "@/contexts/AuthModalsContext";
import { useCart } from "@/contexts/CartContext";
import { useAdmin } from "@/hooks/useAdmin";
import { useCredits } from "@/hooks/useCredits";
import { useLanguage } from "@/contexts/LanguageContext";
import { platforms, Product } from "@/data/products";
import { getTranslatedPlatformName } from "@/i18n/productTranslations";

import LanguageSwitcher from "./LanguageSwitcher";
import HeaderCmsMenu from "./HeaderCmsMenu";

import MultiColorLogo from "./MultiColorLogo";
import logoImg from "@/assets/mehubarim-logo.webp";
const creditCoinImg = "/images/credit-coin.webp";

interface SearchResult {
  product: Product;
  platformSlug: string;
  platformName: string;
}

const SiteHeader = () => {
  const { user, signOut } = useAuth();
  const { openLogin, openSignup } = useAuthModals();
  const { totalItems, openCart } = useCart();
  const { isAdmin } = useAdmin();
  const { balance } = useCredits();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navBarRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [stickySearchOpen, setStickySearchOpen] = useState(false);

  const allProducts = useMemo(() => {
    const results: SearchResult[] = [];
    for (const platform of platforms) {
      for (const product of platform.products) {
        results.push({ product, platformSlug: platform.slug, platformName: platform.name });
      }
    }
    return results;
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return allProducts
      .filter(
        (r) =>
          r.product.name.toLowerCase().includes(q) ||
          r.product.label.toLowerCase().includes(q) ||
          r.product.description.toLowerCase().includes(q) ||
          r.platformName.toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [searchQuery, allProducts]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsSticky(!entry.isIntersecting),
      { threshold: 0, rootMargin: '0px' }
    );
    if (navBarRef.current) observer.observe(navBarRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSelect = (result: SearchResult) => {
    setSearchQuery("");
    setShowResults(false);
    navigate(`/${result.platformSlug}/${result.product.slug}`);
  };

  const searchInput = (
    <div ref={searchRef} className="relative w-full">
      <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary pointer-events-none" />
      {searchQuery && (
        <button
          onClick={() => { setSearchQuery(""); setShowResults(false); }}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      <input
        type="text"
        placeholder={t.searchPlaceholder}
        value={searchQuery}
        onChange={(e) => { setSearchQuery(e.target.value); setShowResults(true); }}
        onFocus={() => searchQuery.trim() && setShowResults(true)}
        className="w-full pr-10 pl-9 py-2.5 rounded-full bg-muted/50 text-foreground placeholder:text-muted-foreground text-sm transition-all duration-300 neon-border border focus:border-primary focus:neon-glow-cyan focus:outline-none"
      />
      {showResults && filtered.length > 0 && (
        <div className="absolute top-full mt-2 w-full neon-card overflow-hidden max-h-[400px] overflow-y-auto z-50">
          {filtered.map((result) => (
            <button
              key={`${result.platformSlug}-${result.product.id}`}
              onClick={() => handleSelect(result)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-primary/10 transition-colors text-start border-b border-border/50 last:border-0"
            >
              <img src={result.product.image} alt={result.product.name} className="w-10 h-10 rounded-lg object-contain bg-muted/50 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{result.product.name}</p>
                <p className="text-xs text-muted-foreground truncate">{result.platformName} · <span className="neon-text-cyan"><CreditPrice amount={result.product.price} /></span></p>
              </div>
            </button>
          ))}
        </div>
      )}
      {showResults && searchQuery.trim() && filtered.length === 0 && (
        <div className="absolute top-full mt-2 w-full neon-card z-50 px-4 py-6 text-center">
          <p className="text-sm text-muted-foreground">No results found</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Helmet>
        <link rel="preload" as="image" href={creditCoinImg} type="image/webp" />
      </Helmet>
      <header className="w-full relative border-b" style={{ background: 'linear-gradient(180deg, hsl(230 25% 9%), hsl(230 25% 7%))', borderColor: 'hsl(174 90% 50% / 0.15)' }}>
      {/* Top bar */}
      <div className="border-b" style={{ borderColor: 'hsl(174 90% 50% / 0.1)', background: 'hsl(230 25% 5% / 0.5)' }}>
        <div className="max-w-[1100px] mx-auto px-4 py-1.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
          </div>
          <div className="flex items-center gap-4 text-sm">
            {user ? (
              <>
                <Link to="/topup" className="neon-text-cyan hover:underline flex items-center gap-1 font-semibold text-xs sm:text-sm">
                  <CreditPrice amount={balance} />
                </Link>
                <Link to="/credits-history" className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs sm:text-sm transition-colors">
                  <History className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t.credits}</span>
                </Link>
                <Link to="/orders" className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs sm:text-sm transition-colors">
                  <ShoppingBag className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t.orders}</span>
                </Link>
                <span className="text-muted-foreground hidden sm:inline">{user.email}</span>
                {isAdmin && (
                  <Link to="/admin" className="neon-text-cyan hover:underline flex items-center gap-1.5 font-medium">
                    <Settings className="w-4 h-4" /> {t.admin}
                  </Link>
                )}
                <button onClick={signOut} className="neon-text-pink hover:underline flex items-center gap-1.5 font-medium">
                  <LogOut className="w-4 h-4" /> {t.logout}
                </button>
              </>
            ) : (
              <>
                <button onClick={openLogin} className="neon-text-cyan hover:underline font-semibold">{t.login}</button>
                <span style={{ color: 'hsl(174 90% 50% / 0.3)' }}>|</span>
                <button onClick={openSignup} className="neon-text-purple hover:underline font-semibold">{t.signup}</button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-4 py-2">
        {/* Logo centered with glow */}
        <div className="flex items-center justify-center mb-2">
          <Link to="/" className="flex items-center group flex-shrink-0" aria-label="מחוברים - דף הבית">
            <MultiColorLogo className="h-24 sm:h-32 transition-transform duration-300 group-hover:scale-105" />
          </Link>
        </div>

        {/* Search + Utility icons row */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <button onClick={openCart} className="w-10 h-10 rounded-lg border flex items-center justify-center transition-all duration-300 relative neon-border neon-border-hover bg-muted/30" aria-label="עגלת קניות">
              <ShoppingCart className="w-5 h-5 text-primary" />
              {totalItems > 0 && (
                <span className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] neon-btn animate-neon-pulse">{totalItems}</span>
              )}
            </button>
            <a href="#" className="w-10 h-10 rounded-lg border flex items-center justify-center transition-all duration-300 neon-border neon-border-hover bg-muted/30" aria-label="עזרה">
              <HelpCircle className="w-5 h-5 text-neon-purple" />
            </a>
          </div>

          <div className="w-full max-w-[260px]">
            {searchInput}
          </div>
        </div>

        {/* Sentinel for sticky detection */}
        <div ref={navBarRef} />

        {/* Platform Icons Row with Credit Coin */}
        <div className="flex items-center gap-3 sm:gap-5">
          <Link
            to="/topup"
            className="flex flex-col items-center gap-1 group transition-all duration-300 hover:-translate-y-1 flex-shrink-0"
            title={t.topUpCredits}
          >
            <div className="relative">
              <img
                src={creditCoinImg}
                alt={t.credits}
                className="w-14 h-14 sm:w-16 sm:h-16 object-contain transition-transform duration-300 group-hover:scale-110"
                style={{ filter: 'drop-shadow(0 0 12px hsl(174 90% 50% / 0.5)) drop-shadow(0 0 25px hsl(174 90% 50% / 0.2))' }}
                loading="eager"
                fetchPriority="high"
                width={64}
                height={64}
              />
              <div className="absolute inset-0 rounded-full animate-pulse" style={{ background: 'radial-gradient(circle, hsl(174 90% 50% / 0.15), transparent 70%)' }} />
            </div>
            <span className="text-[10px] sm:text-xs neon-text-cyan font-bold font-display">{t.credits}</span>
          </Link>

          <div className="w-px h-10 flex-shrink-0" style={{ background: 'linear-gradient(180deg, transparent, hsl(174 90% 50% / 0.3), transparent)' }} />

          <div className="flex items-center justify-center gap-3 sm:gap-5 flex-wrap flex-1">
            {platforms.map((platform) => (
              <Link
                key={platform.id}
                to={`/${platform.slug}`}
                className="flex flex-col items-center gap-1 group transition-all duration-300 hover:-translate-y-1"
                title={getTranslatedPlatformName(platform.slug, lang)}
              >
                <img
                  src={platform.icon}
                  alt={getTranslatedPlatformName(platform.slug, lang)}
                  className="w-12 h-12 sm:w-14 sm:h-14 object-contain transition-transform duration-300 group-hover:scale-110"
                  style={{ filter: 'drop-shadow(0 0 8px hsl(174 90% 50% / 0.3))' }}
                />
                <span className="text-[10px] sm:text-xs text-muted-foreground group-hover:neon-text-cyan transition-colors font-medium">
                  {getTranslatedPlatformName(platform.slug, lang)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <HeaderCmsMenu />

      {/* Bottom glow line */}
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, hsl(174 90% 50% / 0.5), hsl(280 80% 55% / 0.5), hsl(320 85% 55% / 0.3), transparent)' }} />

      {/* Sticky nav bar - appears on scroll */}
      {isSticky && (
        <div
          className="fixed top-0 left-0 right-0 z-50 border-b animate-in slide-in-from-top-2 duration-300"
          style={{
            background: 'linear-gradient(180deg, hsl(230 25% 9% / 0.95), hsl(230 25% 7% / 0.95))',
            borderColor: 'hsl(174 90% 50% / 0.15)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <div className="max-w-[1100px] mx-auto px-4 py-2 flex items-center gap-3 sm:gap-4">
            <Link
              to="/topup"
              className="flex items-center gap-1.5 group flex-shrink-0"
              title={t.topUpCredits}
            >
              <img
                src={creditCoinImg}
                alt={t.credits}
                className="w-8 h-8 object-contain transition-transform duration-300 group-hover:scale-110"
                style={{ filter: 'drop-shadow(0 0 8px hsl(174 90% 50% / 0.4))' }}
              />
              <span className="text-xs neon-text-cyan font-bold hidden sm:inline">{t.credits}</span>
            </Link>

            <div className="w-px h-6 flex-shrink-0" style={{ background: 'linear-gradient(180deg, transparent, hsl(174 90% 50% / 0.3), transparent)' }} />

            <div className="flex items-center gap-2 sm:gap-3 flex-1 justify-center flex-wrap">
              {platforms.map((platform) => (
                <Link
                  key={platform.id}
                  to={`/${platform.slug}`}
                  className="flex items-center gap-1.5 group transition-all duration-200 hover:scale-105 flex-shrink-0"
                  title={getTranslatedPlatformName(platform.slug, lang)}
                >
                  <img
                    src={platform.icon}
                    alt={getTranslatedPlatformName(platform.slug, lang)}
                    className="w-7 h-7 sm:w-8 sm:h-8 object-contain"
                    style={{ filter: 'drop-shadow(0 0 6px hsl(174 90% 50% / 0.3))' }}
                  />
                  <span className="text-[10px] sm:text-xs text-muted-foreground group-hover:neon-text-cyan transition-colors font-medium hidden md:inline">
                    {getTranslatedPlatformName(platform.slug, lang)}
                  </span>
                </Link>
              ))}
            </div>

            <button
              onClick={() => setStickySearchOpen((v) => !v)}
              className="w-9 h-9 rounded-lg border flex items-center justify-center transition-all duration-300 neon-border neon-border-hover bg-muted/30 flex-shrink-0"
            >
              <Search className="w-4 h-4 text-primary" />
            </button>
          </div>

          {/* Expandable search row */}
          {stickySearchOpen && (
            <div className="max-w-[1100px] mx-auto px-4 pb-2 animate-in slide-in-from-top-1 duration-200">
              <div className="max-w-[400px] mx-auto">
                {searchInput}
              </div>
            </div>
          )}

          <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, hsl(174 90% 50% / 0.4), hsl(280 80% 55% / 0.4), transparent)' }} />
        </div>
      )}
    </header>
    </>
  );
};

export default SiteHeader;
